import React from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert, Linking } from 'react-native';
import { Colors, SIZES } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { GlassView } from 'expo-glass-effect';
import { useAppStore } from '../../store/useAppStore';
import { useRouter } from 'expo-router';
import { ratingDisplay } from '../../services/ratingUtils';

/**
 * Devuelve el teléfono marcable del chofer, o null si NO hay uno de verdad.
 *
 * POR QUÉ: el documento que crea la aprobación (`onboardDriverToCRM`) no
 * arrastra el teléfono del candidato, así que `driver.phone` llega `undefined`
 * para todo chofer aprobado. El botón "Llamar" armaba `phone.replace(...)`
 * dentro del onPress → TypeError SÍNCRONO que sube al manejador global y
 * CIERRA la app sobre un botón visible (uno de los motivos por los que Google
 * Play ya rechazó esta cuenta). Aquí normalizamos sin poder lanzar: cualquier
 * cosa que no sea un teléfono con al menos 7 dígitos devuelve null, y sin
 * teléfono el botón ni se pinta.
 */
function telefonoMarcable(bruto: unknown): string | null {
  const texto = typeof bruto === 'number' ? String(bruto) : (typeof bruto === 'string' ? bruto : '');
  // Conservamos el '+' inicial (internacional) y descartamos el resto del ruido.
  const limpio = texto.trim().replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
  const digitos = limpio.replace(/\D/g, '');
  return digitos.length >= 7 ? limpio : null;
}

/**
 * Devuelve el uid de Firebase del chofer, o null si el documento no lo trae.
 *
 * POR QUÉ: el chat de rentas arma su chatId con los DOS uids
 * (`${ownerId}__${renterId}`). Al abrirlo desde aquí no se pasaba ningún uid,
 * así que el chatId quedaba vacío: el listener no se suscribía y `sendMessage`
 * se salía en silencio — el dueño escribía y el mensaje se perdía sin aviso
 * ("Unresponsive UI elements"). Se leen solo campos que significan un uid de
 * cuenta. `candidateId` va de ÚLTIMO a propósito: en el flujo real (enlace
 * mágico) el documento del candidato se guarda con el uid como id, así que ese
 * campo ES el uid; si algún día llegara de otro origen sin uid, preferimos los
 * campos explícitos. Si no hay ninguno, no hay chat y se dice.
 */
function uidDelChofer(driver: any): string | null {
  const posibles = [driver?.candidateUid, driver?.driverUid, driver?.uid, driver?.authUid, driver?.candidateId];
  for (const v of posibles) {
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return null;
}

export default function DriversScreen() {
  const router = useRouter();
  const drivers = useAppStore((state) => state.rentCarDrivers);
  const setActiveChatPartner = useAppStore((state) => state.setActiveChatPartner);
  const ownerUid = useAppStore((state) => state.userFirebaseUid);
  const userRole = useAppStore((state) => state.userRole);

  // El chat solo puede existir si YO soy el dueño identificado: el chatId se
  // arma con mi uid del lado del dueño (rent-car-chat.tsx). Sin sesión no hay
  // conversación posible, y es mejor no ofrecerla que ofrecer una muerta.
  const puedoAbrirChats = !!ownerUid && userRole === 'RENT_CAR_OWNER';

  const handleCallDriver = (name: string, telefono: string) => {
    Alert.alert(
      'Llamar al Chofer',
      `¿Deseas iniciar una llamada telefónica con ${name} al número ${telefono}?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Llamar',
          onPress: () => {
            // try/catch además del .catch(): si `openURL` lanza de forma
            // síncrona (o no devuelve promesa), la excepción NO puede subir al
            // manejador global y tumbar la app.
            try {
              const p: any = Linking.openURL(`tel:${telefono}`);
              if (p && typeof p.catch === 'function') {
                p.catch(() => {
                  Alert.alert('Error', 'No se pudo iniciar la llamada desde este dispositivo.');
                });
              }
            } catch {
              Alert.alert('Error', 'No se pudo iniciar la llamada desde este dispositivo.');
            }
          }
        }
      ]
    );
  };

  const handleChatDriver = (name: string, vehicle: string, driverUid: string) => {
    // Guardia doble: aunque el botón solo se pinta con los datos completos,
    // la función tampoco abre un chat que no puede funcionar.
    if (!puedoAbrirChats || !driverUid) return;
    // `partnerUid` es lo que faltaba: sin él, rent-car-chat.tsx no puede armar
    // el chatId y la conversación nace muerta.
    setActiveChatPartner({ name, vehicle, role: 'DRIVER', partnerUid: driverUid });
    router.push('/rent-car-chat');
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <Text style={styles.title}>Mis Choferes</Text>
        <Text style={styles.subtitle}>Supervisa el rendimiento y contacto de tu flota activa ({drivers.length})</Text>

        {drivers.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="people-outline" size={60} color={Colors.textSecondary} style={{ marginBottom: 16 }} />
            <Text style={styles.emptyText}>No tienes choferes vinculados.</Text>
            <Text style={styles.emptySubText}>
              Comparte tu Enlace Mágico desde el panel de CRM para invitar a nuevos choferes a registrarse.
            </Text>
          </View>
        ) : (
          drivers.map((driver) => {
            // Nota real o "Nuevo": el alta escribe rating '5.0' a fuego y nadie
            // lo actualiza nunca, así que las estrellas eran una afirmación
            // falsa (⭐5.0 junto a "Viajes: 0", contradiciéndose en la misma
            // tarjeta). `ratingDisplay` — el mismo ayudante que usa el resto de
            // la app — solo devuelve número cuando hay calificaciones reales.
            const nota = ratingDisplay(driver.rating, driver.ratingCount);
            const telefono = telefonoMarcable(driver.phone);
            const driverUid = uidDelChofer(driver);
            const puedeChatear = puedoAbrirChats && !!driverUid;
            return (
            <GlassView key={driver.id} style={styles.driverCard} colorScheme="dark">
              <View style={styles.cardHeader}>
                <View style={styles.infoCol}>
                  <Text style={styles.driverName}>{driver.name}</Text>
                  <Text style={styles.vehicleLabel}>Vehículo Asignado</Text>
                  <Text style={styles.vehicleName}>{driver.vehicle}</Text>
                </View>
                <View style={styles.ratingBadge}>
                  {!nota.isNew && <Ionicons name="star" size={14} color="#FFD700" />}
                  <Text style={[styles.ratingText, nota.isNew && { marginLeft: 0 }]}>{nota.label}</Text>
                </View>
              </View>

              <View style={styles.divider} />

              {/* Se quitó "Asistencia 100%": era un literal escrito a fuego que
                  ningún código calcula en todo el proyecto. No hay dato honesto
                  que poner en su lugar, así que la métrica desaparece. */}
              <View style={styles.metricsRow}>
                <View style={styles.metricItem}>
                  <Text style={styles.metricValue}>{driver.trips || 0}</Text>
                  <Text style={styles.metricLabel}>Viajes</Text>
                </View>
                <View style={styles.verticalDivider} />
                <View style={styles.metricItem}>
                  <Text style={styles.metricValue}>{driver.status}</Text>
                  <Text style={styles.metricLabel}>Estado</Text>
                </View>
              </View>

              <View style={styles.actionsRow}>
                {telefono ? (
                  <TouchableOpacity
                    style={[styles.actionBtn, { backgroundColor: 'rgba(76, 175, 80, 0.1)', borderColor: 'rgba(76, 175, 80, 0.3)', borderWidth: 1 }]}
                    onPress={() => handleCallDriver(driver.name, telefono)}
                  >
                    <Ionicons name="call" size={18} color="#4CAF50" />
                    <Text style={[styles.actionBtnText, { color: '#4CAF50' }]}>Llamar</Text>
                  </TouchableOpacity>
                ) : (
                  // Sin teléfono NO se pinta el botón: un botón que no puede
                  // llamar es un botón que engaña. Se dice qué falta.
                  <View style={styles.infoChip}>
                    <Ionicons name="call-outline" size={16} color={Colors.textSecondary} />
                    <Text style={styles.infoChipText}>Sin teléfono registrado</Text>
                  </View>
                )}

                {puedeChatear ? (
                  <TouchableOpacity
                    style={[styles.actionBtn, { backgroundColor: Colors.primary }]}
                    onPress={() => handleChatDriver(driver.name, driver.vehicle, driverUid as string)}
                  >
                    <Ionicons name="chatbubbles" size={18} color="#FFF" />
                    <Text style={[styles.actionBtnText, { color: '#FFF' }]}>Mensajes</Text>
                  </TouchableOpacity>
                ) : (
                  // Sin el uid del chofer el chat no puede existir (el chatId se
                  // arma con los dos uids). Mejor decirlo que abrir un hilo
                  // donde los mensajes se pierden.
                  <View style={styles.infoChip}>
                    <Ionicons name="chatbubbles-outline" size={16} color={Colors.textSecondary} />
                    <Text style={styles.infoChipText}>Chat no disponible</Text>
                  </View>
                )}
              </View>
            </GlassView>
            );
          })
        )}

        <View style={{ height: 100 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  container: {
    padding: SIZES.padding,
    paddingTop: 60,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: Colors.text,
  },
  subtitle: {
    fontSize: SIZES.medium,
    color: Colors.textSecondary,
    marginBottom: 30,
    lineHeight: 22,
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 40,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
  },
  emptyText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubText: {
    fontSize: 14,
    color: Colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  driverCard: {
    padding: 20,
    borderRadius: 24,
    marginBottom: 20,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  infoCol: {
    flex: 1,
  },
  driverName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 6,
  },
  vehicleLabel: {
    fontSize: 11,
    color: Colors.textSecondary,
    textTransform: 'uppercase',
    fontWeight: '600',
    marginTop: 4,
  },
  vehicleName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: Colors.primary,
    marginTop: 2,
  },
  ratingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 215, 0, 0.15)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
  },
  ratingText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#FF9800',
    marginLeft: 4,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
    marginVertical: 16,
  },
  metricsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  metricItem: {
    flex: 1,
    alignItems: 'center',
  },
  metricValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Colors.text,
  },
  metricLabel: {
    fontSize: 11,
    color: Colors.textSecondary,
    marginTop: 4,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  verticalDivider: {
    width: 1,
    height: 30,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  actionBtn: {
    flex: 1,
    height: 48,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  actionBtnText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  // Ocupa el sitio de un botón que NO existe porque falta el dato. No es
  // pulsable a propósito: explica, no promete.
  infoChip: {
    flex: 1,
    height: 48,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 8,
    backgroundColor: 'rgba(128,128,128,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(128,128,128,0.25)',
  },
  infoChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.textSecondary,
    flexShrink: 1,
  },
});
